import { File as FileIcon, X, CircleNotch, WarningCircle } from "@phosphor-icons/react";
import type { PendingAttachment } from "../../utils/chatUploads";

interface AttachmentPreviewProps {
  attachments: PendingAttachment[];
  onRemove?: (id: string) => void;
}

/**
 * Pre-send thumbnail row that appears above the chat input when the user has
 * attached files. Each tile shows: image preview or PDF icon, filename,
 * upload status, and a remove button.
 */
export default function AttachmentPreview({ attachments, onRemove }: AttachmentPreviewProps) {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 px-1 pb-2">
      {attachments.map((att) => {
        const isImage = att.file.type.startsWith("image/");
        return (
          <div
            key={att.id}
            className="relative flex items-center gap-2 max-w-[200px] pr-7 pl-1.5 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            {isImage ? (
              <img
                src={att.previewUrl}
                alt={att.file.name}
                className="w-10 h-10 object-cover rounded"
              />
            ) : (
              <div className="w-10 h-10 flex items-center justify-center bg-pink-50 dark:bg-pink-900/30 rounded text-pink-500">
                <FileIcon size={20} weight="fill" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                {att.file.name}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                {att.status === "uploading" && (
                  <>
                    <CircleNotch size={10} className="animate-spin" />
                    Uploading…
                  </>
                )}
                {att.status === "ready" && "Ready"}
                {att.status === "error" && (
                  <span className="text-red-500 flex items-center gap-1">
                    <WarningCircle size={10} />
                    {att.error || "Failed"}
                  </span>
                )}
                {att.status === "pending" && `${(att.file.size / 1024).toFixed(0)} KB`}
              </p>
            </div>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(att.id)}
                className="absolute top-1 right-1 p-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                aria-label="Remove attachment"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
